import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  discoverMostTraded,
  discoverNews,
  discoverRisersFallers,
  discoverVolatile,
} from "@/lib/api";
import { formatPrice, friendlyName } from "@/lib/symbols";
import { ArrowRight, ArrowUpRight, ArrowDownRight, Newspaper } from "lucide-react";

const CLASS_LABEL = {
  stock: "Shares",
  etf: "ETF",
  crypto: "Crypto",
  fx: "Forex",
  future: "Commodities",
};

export default function DiscoverPage() {
  const [rf, setRf] = useState(null);
  const [vol, setVol] = useState(null);
  const [most, setMost] = useState(null);
  const [news, setNews] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    Promise.all([
      discoverRisersFallers(5).catch(() => null),
      discoverVolatile(6).catch(() => null),
      discoverMostTraded().catch(() => null),
      discoverNews(10).catch(() => null),
    ]).then(([a, b, c, d]) => {
      if (!alive) return;
      setRf(a); setVol(b); setMost(c); setNews(d);
    }).catch((e) => alive && setErr(String(e)));
    return () => { alive = false; };
  }, []);

  const goDesk = (sym) => nav(`/desk?symbol=${encodeURIComponent(sym)}`);

  return (
    <div className="mx-auto max-w-[1400px] space-y-6" data-testid="discover-page">
      <header>
        <h1 className="font-heading text-3xl tracking-widest text-[var(--aurion-gold)]">
          Discover
        </h1>
        <p className="mt-1 text-xs text-[var(--aurion-muted)]">
          Real-data browse surface — powered by NUMIROCK's screener, live
          yfinance quotes, and platform-wide trade aggregates. Nothing here is
          fabricated. Colors: <span className="text-up">gold = up</span>{" "}
          · <span className="text-down">metallic-purple = down</span>.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[1.15fr_1fr]">
        <section className="space-y-4">
          <MostTradedBlock most={most} />
          <RisersFallersBlock rf={rf} onPick={goDesk} />
          <VolatileBlock vol={vol} onPick={goDesk} />
        </section>
        <section>
          <NewsBlock news={news} onPick={goDesk} />
        </section>
      </div>

      {err ? (
        <p className="text-xs text-[var(--aurion-red)]" data-testid="discover-error">
          {err}
        </p>
      ) : null}
    </div>
  );
}

function MostTradedBlock({ most }) {
  const items = most?.items || [];
  const sourceLabel = most?.source === "universe_volume_weights_fallback"
    ? "by market volume (fallback)" : "by trade count · last 7 days";
  return (
    <div
      data-testid="discover-most-traded"
      className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40 p-4"
    >
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="font-heading text-sm tracking-widest text-[var(--aurion-text)]">
          Most traded
        </h2>
        <span className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
          {sourceLabel}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[var(--aurion-muted)]">loading…</p>
      ) : (
        <ul className="space-y-2">
          {items.map((row) => (
            <li key={row.asset_class} data-testid={`most-traded-${row.asset_class}`}>
              <div className="mb-1 flex items-baseline justify-between text-xs">
                <span className="text-[var(--aurion-text)]">
                  {CLASS_LABEL[row.asset_class] || row.asset_class}
                </span>
                <span className="font-mono tabular-nums text-[var(--aurion-muted-strong)]">
                  {row.share_pct.toFixed(1)}%
                  {row.trade_count_7d != null
                    ? ` · ${row.trade_count_7d} trades`
                    : ""}
                </span>
              </div>
              <div className="h-[6px] w-full bg-[var(--aurion-panel-2)]">
                <div
                  className="h-full"
                  style={{
                    width: `${Math.max(2, row.share_pct)}%`,
                    background: "linear-gradient(90deg, var(--aurion-gold) 0%, var(--aurion-purple-metal) 100%)",
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AssetTable({ testid, title, rows, onPick, changeKey = "day_change_pct", extra }) {
  return (
    <div
      data-testid={testid}
      className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
    >
      <div className="flex items-baseline justify-between border-b border-[var(--aurion-border)] px-3 py-2">
        <h2 className="font-heading text-sm tracking-widest text-[var(--aurion-text)]">
          {title}
        </h2>
        {extra}
      </div>
      {!rows ? (
        <p className="p-3 text-xs text-[var(--aurion-muted)]">loading…</p>
      ) : rows.length === 0 ? (
        <p className="p-3 text-xs text-[var(--aurion-muted)]">no data</p>
      ) : (
        <ul>
          {rows.map((r) => {
            const chg = r[changeKey];
            const isUp = chg == null ? null : chg >= 0;
            return (
              <li
                key={r.symbol}
                data-testid={`${testid}-row-${r.symbol}`}
                className="flex cursor-pointer items-center justify-between border-b border-[var(--aurion-border)]/40 px-3 py-2 last:border-b-0 hover:bg-white/[0.03]"
                onClick={() => onPick?.(r.symbol)}
              >
                <div className="flex min-w-0 items-baseline gap-3">
                  <span className="font-heading text-xs tracking-widest text-[var(--aurion-text)]">
                    {r.symbol}
                  </span>
                  <span className="truncate text-xs text-[var(--aurion-muted-strong)]">
                    {friendlyName(r.symbol, r.name)}
                  </span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-xs tabular-nums text-[var(--aurion-text-dim)]">
                    {formatPrice(r.symbol, r.last_close)}
                  </span>
                  {r.volatility_pct != null ? (
                    <span
                      className="font-mono text-xs tabular-nums text-[var(--aurion-muted-strong)]"
                      title="20-bar stdev of daily returns"
                    >
                      σ {r.volatility_pct.toFixed(2)}%
                    </span>
                  ) : null}
                  {chg != null ? (
                    <span
                      className={`font-mono text-xs tabular-nums ${isUp ? "text-up" : "text-down"}`}
                      data-testid={`${testid}-chg-${r.symbol}`}
                    >
                      {isUp ? "+" : ""}
                      {chg.toFixed(2)}%
                    </span>
                  ) : null}
                  <ArrowRight className="h-3 w-3 text-[var(--aurion-muted)]" />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RisersFallersBlock({ rf, onPick }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <AssetTable
        testid="discover-risers"
        title="Daily risers"
        rows={rf?.risers}
        onPick={onPick}
        extra={<ArrowUpRight className="h-3 w-3 text-up" />}
      />
      <AssetTable
        testid="discover-fallers"
        title="Daily fallers"
        rows={rf?.fallers}
        onPick={onPick}
        extra={<ArrowDownRight className="h-3 w-3 text-down" />}
      />
    </div>
  );
}

function VolatileBlock({ vol, onPick }) {
  return (
    <AssetTable
      testid="discover-volatile"
      title="Most volatile (1-month σ of daily returns)"
      rows={vol?.items}
      onPick={onPick}
    />
  );
}

function NewsBlock({ news, onPick }) {
  const items = news?.items || [];
  return (
    <div
      data-testid="discover-news"
      className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
    >
      <div className="flex items-baseline justify-between border-b border-[var(--aurion-border)] px-3 py-2">
        <h2 className="flex items-center gap-2 font-heading text-sm tracking-widest text-[var(--aurion-text)]">
          <Newspaper className="h-3.5 w-3.5" /> Major news
        </h2>
        <span className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
          yfinance · best-effort
        </span>
      </div>
      {news == null ? (
        <p className="p-3 text-xs text-[var(--aurion-muted)]">loading…</p>
      ) : news.available === false ? (
        <p
          className="p-3 text-xs text-[var(--aurion-muted)]"
          data-testid="discover-news-unavailable"
        >
          News unavailable right now — {news.reason || "provider returned no items"}.
        </p>
      ) : (
        <ul>
          {items.map((n, i) => (
            <li
              key={`${n.url}-${i}`}
              className="border-b border-[var(--aurion-border)]/40 px-3 py-2 last:border-b-0"
              data-testid={`discover-news-item-${i}`}
            >
              <a
                href={n.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[var(--aurion-text)] hover:text-[var(--aurion-gold)]"
              >
                {n.title}
              </a>
              <div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                <span>
                  {n.provider}
                  {n.symbol ? (
                    <>
                      {" · "}
                      <button
                        type="button"
                        className="text-[var(--aurion-gold)] hover:underline"
                        onClick={(e) => { e.preventDefault(); onPick?.(n.symbol); }}
                      >
                        {n.symbol}
                      </button>
                    </>
                  ) : null}
                </span>
                <span>{formatWhen(n.published_at)}</span>
              </div>
            </li>
          ))}
          {items.length === 0 ? (
            <li className="p-3 text-xs text-[var(--aurion-muted)]">
              No fresh headlines returned.
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const min = (Date.now() - d.getTime()) / 60000;
    if (min < 60) return `${Math.max(1, Math.round(min))}m ago`;
    if (min < 60 * 24) return `${Math.round(min / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}
